import React, { useMemo, useState, useEffect } from "react";
import { showError } from "toast/toast";
import ProjectFilePreviewModal, { type ProjectFilePreviewModalData } from "components/ProjectFilePreviewModal";
import { ViewIconButton, DownloadIconButton, DeleteIconButton } from "components/DatatablesActionIcons";

type InstructionsMode = "instructions" | "instructions_files";

type InstructionAttachment = {
  id?: string;
  /** Server file id when attachment came from API (used for delete). */
  file_id?: string;
  name: string;
  sizeLabel?: string;
  previewUrl?: string;
  href?: string;
  dateLabel?: string;
};

type LoginType = "signage" | "brokerage" | "agent";

type LocalInstructionAttachment = InstructionAttachment & {
  localId: string;
  file: File;
  previewUrl: string;
};

type InstructionsCreateCardProps = {
  title?: string;
  value: string;                         // controlled value from parent (e.g., formik.values.notes)
  onChange: (v: string) => void;         // push changes to parent (e.g., formik.setFieldValue)
  placeholder?: string;
  rows?: number;
  className?: string;
  iconUrl?: string;                      // url for your icon (e.g., OrderNotes)
  iconColor?: string;                    // mask color
  iconBackground?: string;               // chip bg
  mode?: InstructionsMode;
  attachments?: InstructionAttachment[];
  onFilesSelected?: (files: FileList) => void;
  onAttachmentPreview?: (attachment: InstructionAttachment) => void;
  onAttachmentDownload?: (attachment: InstructionAttachment) => void;
  onAttachmentRemove?: (attachment: InstructionAttachment) => void;
  /** Fires whenever local (not-yet-uploaded) files change — for parent to upload after order create. */
  onLocalInstructionFilesChange?: (items: { id: string; file: File }[]) => void;
  loginType?: LoginType;
};

const InstructionsCreateCard: React.FC<InstructionsCreateCardProps> = ({
  title = "Instructions",
  value,
  onChange,
  placeholder = "Add instructions here",
  rows = 4,
  className = "",
  iconUrl,
  iconColor = "#10B981",
  iconBackground = "#EAF7F1",
  mode = "instructions",
  attachments = [],
  onFilesSelected,
  onAttachmentPreview,
  onAttachmentDownload,
  onAttachmentRemove,
  onLocalInstructionFilesChange,
  loginType,
}) => {
  const [localAttachments, setLocalAttachments] = useState<LocalInstructionAttachment[]>([]);
  const [previewData, setPreviewData] = useState<ProjectFilePreviewModalData | null>(null);
  const uploadInputId = `create-instructions-upload-${title.replace(/\s+/g, "-").toLowerCase()}`;
  const isFilesMode = mode === "instructions_files";
  const MAX_FILES = 3;
  const MAX_FILE_SIZE_BYTES = 3 * 1024 * 1024;
  const MAX_TOTAL_BYTES = 9 * 1024 * 1024;

  useEffect(() => {
    return () => {
      localAttachments.forEach((item) => URL.revokeObjectURL(item.previewUrl));
    };
  }, [localAttachments]);

  useEffect(() => {
    if (!isFilesMode || !onLocalInstructionFilesChange) return;
    onLocalInstructionFilesChange(localAttachments.map(({ id, file }) => ({ id, file })));
  }, [localAttachments, isFilesMode, onLocalInstructionFilesChange]);

  const allAttachments = useMemo(
    () => [...attachments, ...localAttachments.map(({ localId, file, previewUrl, ...rest }) => rest)],
    [attachments, localAttachments]
  );

  const formatSizeLabel = (bytes: number) => {
    if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${Math.round(bytes / 1024)} KB`;
  };

  const resolvedLoginType = useMemo<LoginType>(() => {
    if (loginType) return loginType;
    return "agent";
  }, [loginType]);

  const canShowDelete = resolvedLoginType === "signage";

  const getAttachmentIconClass = (fileName: string) => {
    const ext = String(fileName || "").split(".").pop()?.toLowerCase() || "";
    if (ext === "pdf") return "ri-file-text-line text-danger fs-20";
    if (["png", "jpg", "jpeg", "gif", "webp", "svg", "bmp"].includes(ext)) return "ri-file-image-line text-primary fs-20";
    return "ri-file-line text-secondary fs-20";
  };

  const handleFilesSelected = (files: FileList) => {
    const fileArray = Array.from(files);
    const existingCount = allAttachments.length;

    if (existingCount + fileArray.length > MAX_FILES) {
      showError("You can upload up to 3 files only.");
      return;
    }

    const oversize = fileArray.find((f) => f.size > MAX_FILE_SIZE_BYTES);
    if (oversize) {
      showError(`Each file must be 3MB or less. "${oversize.name}" is too large.`);
      return;
    }

    const selectedTotal = fileArray.reduce((sum, f) => sum + f.size, 0);
    if (selectedTotal > MAX_TOTAL_BYTES) {
      showError("Total upload size cannot exceed 9MB.");
      return;
    }

    const newLocal = fileArray.map((file) => ({
      id: `local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      localId: `local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      name: file.name,
      sizeLabel: formatSizeLabel(file.size),
      file,
      previewUrl: URL.createObjectURL(file),
    }));
    setLocalAttachments((prev) => [...prev, ...newLocal]);
    onFilesSelected?.(files);
  };

  const removeAttachment = (attachment: InstructionAttachment) => {
    const local = localAttachments.find((item) => item.id === attachment.id);
    if (local) {
      URL.revokeObjectURL(local.previewUrl);
      setLocalAttachments((prev) => prev.filter((item) => item.id !== attachment.id));
    }
    onAttachmentRemove?.(attachment);
  };

  const previewAttachment = (attachment: InstructionAttachment) => {
    const local = localAttachments.find((item) => item.id === attachment.id);
    const modalData: ProjectFilePreviewModalData = {
      name: attachment.name,
      previewUrl: local?.previewUrl || attachment.previewUrl || "",
      href: local?.previewUrl || attachment.href || attachment.previewUrl || "",
      sizeLabel: attachment.sizeLabel,
      dateLabel: attachment.dateLabel,
    };
    setPreviewData(modalData);
    onAttachmentPreview?.(attachment);
  };

  const downloadAttachment = (attachment: InstructionAttachment) => {
    const local = localAttachments.find((item) => item.id === attachment.id);
    if (local) {
      const a = document.createElement("a");
      a.href = local.previewUrl;
      a.download = local.name;
      a.click();
    } else if (attachment.href || attachment.previewUrl) {
      const a = document.createElement("a");
      a.href = attachment.href || attachment.previewUrl || "";
      a.download = attachment.name;
      a.target = "_blank";
      a.rel = "noopener noreferrer";
      a.click();
    }
    onAttachmentDownload?.(attachment);
  };

  return (
    <div className={`card shadow-lg shadow-border-1 ${className}`}>
      <div className="pt-4 px-3" >
        <div className="d-flex justify-content-between align-items-center">
          <h6 className="mb-0 d-flex align-items-center fw-semibold">
            {iconUrl ? (
              <span
                aria-hidden="true"
                className="image-icons"
                style={{
                  width: 28,
                  height: 28,
                  display: "inline-block",
                  backgroundColor: iconColor,
                  WebkitMaskImage: `url(${iconUrl})`,
                  maskImage: `url(${iconUrl})`,
                  WebkitMaskRepeat: "no-repeat",
                  maskRepeat: "no-repeat",
                  WebkitMaskPosition: "center",
                  maskPosition: "center",
                  WebkitMaskSize: "contain",
                  maskSize: "contain",
                  borderRadius: 8,
                  background: iconColor,
                }}
              />
            ) : null}
            <span className="ms-2">{title}</span>
          </h6>
          {/* No edit/save buttons for create flow */}
        </div>
      </div>

      <div className="card-body border-0">
        <textarea
          className="form-control bg-light rounded-3 p-3 fs-6 lh-base"
          style={{ resize: "none" }}
          rows={rows}
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}   // ← direct pass-up
        />

        {isFilesMode && (
          <div className="mt-3">
            <hr className="my-3" style={{color: "#b2b2b2"}}/>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <div className="d-flex align-items-center gap-2 fw-medium">
                <i className="ri-attachment-2" />
                <span>Attachments</span>
                <span className="badge rounded-pill text-bg-secondary">{allAttachments.length}</span>
              </div>
              <label htmlFor={uploadInputId} className="btn btn-link p-0 text-decoration-none fw-medium">
                <i className="ri-upload-cloud-2-line me-1" />
                Upload File
              </label>
              <input
                id={uploadInputId}
                type="file"
                className="d-none"
                multiple
                onChange={(e) => {
                  if (e.target.files && e.target.files.length > 0) handleFilesSelected(e.target.files);
                  e.currentTarget.value = "";
                }}
              />
            </div>

            <div className="d-flex flex-column gap-2">
              {allAttachments.map((file, index) => (
                <div
                  key={file.id || `${file.name}-${index}`}
                  className="d-flex align-items-center justify-content-between rounded-3 px-3 py-2 notes-attachment-row"
                >
                  <div className="d-flex align-items-center gap-2 overflow-hidden">
                    <i className={getAttachmentIconClass(file.name)} />
                    <div className="text-truncate">
                      <div className="text-truncate fw-medium">{file.name}</div>
                      {file.sizeLabel ? <div className="small text-muted">{file.sizeLabel}</div> : null}
                    </div>
                  </div>
                  <div className="d-flex align-items-center gap-2 ms-2 notes-attachment-actions">
                    <ViewIconButton
                      size={30}
                      title="View"
                      ariaLabel="View file"
                      onClick={() => previewAttachment(file)}
                    />
                    <DownloadIconButton
                      size={30}
                      title="Download"
                      ariaLabel="Download file"
                      onClick={() => downloadAttachment(file)}
                    />
                    {canShowDelete ? (
                      <DeleteIconButton
                        size={30}
                        title="Remove"
                        ariaLabel="Remove file"
                        onClick={() => removeAttachment(file)}
                      />
                    ) : null}
                  </div>
                </div>
              ))}
            </div>

            <label
              htmlFor={uploadInputId}
              className="mt-3 rounded-3 text-center py-4 w-100 cursor-pointer notes-upload-dropzone"
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
                  handleFilesSelected(e.dataTransfer.files);
                }
              }}
            >
              <i className="ri-upload-cloud-2-line fs-4 text-muted" />
              <div className="fw-medium" style={{ color: "#2563EB" }}>Click to upload <span className="text-muted">or drag & drop</span></div>
              <div className="small text-muted">PDF, PNG, JPG, XLSX up to 3MB each (max 3 files / 9MB total)</div>
            </label>
          </div>
        )}
      </div>

      <style>{`
        .card { transition: all 0.2s ease-in-out; }
        .image-icons { background: ${iconBackground}; }
        .notes-upload-dropzone {
          border: 2px dashed #d1d5db;
          background-color: #f3f4f6;
          transition: background-color 0.2s ease-in-out, border-color 0.2s ease-in-out;
        }
        .notes-upload-dropzone:hover {
          background-color: #e5e7eb;
          border-color: #9ca3af;
        }
        .notes-attachment-row {
          border: 1px solid transparent;
          background-color: #f3f4f6;
          transition: background-color 0.15s ease-in-out, border-color 0.15s ease-in-out;
        }
        .notes-attachment-row:hover {
          border-color: #d1d5db;
          background-color: #eceff3;
        }
        .notes-attachment-actions {
          opacity: 0;
          pointer-events: none;
          transition: opacity 0.15s ease-in-out;
        }
        .notes-attachment-row:hover .notes-attachment-actions {
          opacity: 1;
          pointer-events: auto;
        }
      `}</style>

      <ProjectFilePreviewModal
        data={previewData}
        onClose={() => setPreviewData(null)}
        onDownload={() => {
          if (!previewData) return;
          downloadAttachment({
            name: previewData.name,
            previewUrl: previewData.previewUrl,
            href: previewData.href,
            sizeLabel: previewData.sizeLabel,
            dateLabel: previewData.dateLabel,
          });
        }}
      />
    </div>
  );
};

export default InstructionsCreateCard;
